/**
 * @file routes/_app/settings/materials.tsx
 * @description 資材庫管理頁面（Task 70）。
 *
 * 功能：
 * - 列表查詢（category / name / isActive）
 * - 新增/編輯資材（category / name / notes / ingredients/approvedSchemes JSON）
 * - 軟刪除
 *
 * 視覺：ListPageShell + FilterBar + StatusPill
 */

import { createFileRoute } from '@tanstack/react-router';
import { message } from '@/lib/antd-app';
import { Button, Col, Form, Input, Modal, Row, Select, Space, Switch, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Plus, Search, Edit3, Package, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { ListPageShell, PageMetaItem, FilterBar, StatusPill } from '@/components/shell';
import { color, text, weight } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/settings/materials')({ component: MaterialsPage });

/**
 * 資材實體類型（對應後端 materials 表）。
 */
interface Material {
  id: number;
  category: string;
  name: string;
  ingredients: unknown;
  approvedSchemes: unknown;
  notes: string | null;
  isActive: boolean;
}

/** 後端分頁回應結構 */
interface MaterialListResponse {
  items: Material[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** 資材庫管理頁面 */
function MaterialsPage() {
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [searchText, setSearchText] = useState('');
  const [filterCategory, setFilterCategory] = useState<string | undefined>();
  const [form] = Form.useForm<Record<string, unknown>>();

  /** 查詢資材列表 */
  const { data, isLoading } = useQuery({
    queryKey: ['settings-materials', searchText, filterCategory],
    queryFn: () => {
      const params = new URLSearchParams();
      if (searchText) params.set('search', searchText);
      if (filterCategory) params.set('category', filterCategory);
      return apiClient.get<MaterialListResponse>(`/api/settings/materials?${params.toString()}`);
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const activeCount = items.filter((m) => m.isActive).length;

  /** 從已有資材提取分類選項 */
  const categoryOptions = [...new Set(items.map((m) => m.category))].map((c) => ({
    value: c,
    label: c,
  }));

  /** 開啟新增表單 */
  const handleAdd = () => {
    setEditingMaterial(null);
    form.resetFields();
    setModalOpen(true);
  };

  /** 開啟編輯表單 */
  const handleEdit = (record: Material) => {
    setEditingMaterial(record);
    form.setFieldsValue({
      ...record,
      ingredientsJson: record.ingredients ? JSON.stringify(record.ingredients, null, 2) : '',
      approvedSchemesJson: record.approvedSchemes
        ? JSON.stringify(record.approvedSchemes, null, 2)
        : '',
    });
    setModalOpen(true);
  };

  /** 表格欄位 */
  const columns: ColumnsType<Material> = [
    {
      title: '分類',
      dataIndex: 'category',
      width: 130,
      render: (v: string) => (
        <StatusPill tone="brand" size="sm" variant="outline">
          {v}
        </StatusPill>
      ),
    },
    {
      title: '名稱',
      dataIndex: 'name',
      render: (v: string) => (
        <span style={{ fontWeight: weight.medium, color: color.text.primary }}>{v}</span>
      ),
    },
    {
      title: '備注',
      dataIndex: 'notes',
      ellipsis: true,
      render: (v: string | null) => (
        <span style={{ fontSize: text.sm, color: color.text.secondary }}>{v || '—'}</span>
      ),
    },
    {
      title: '狀態',
      dataIndex: 'isActive',
      width: 80,
      render: (v: boolean) => (
        <StatusPill tone={v ? 'success' : 'muted'} size="sm" dot>
          {v ? '啟用' : '停用'}
        </StatusPill>
      ),
    },
    {
      title: '操作',
      key: '_actions',
      width: 70,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="編輯">
          <Button
            type="text"
            size="small"
            icon={<Edit3 size={14} />}
            onClick={() => handleEdit(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <ListPageShell
        title="資材庫管理"
        description="維護各驗證方案中允許使用的農業資材，包括農藥、肥料及添加物。"
        meta={
          <>
            <PageMetaItem icon={<Package size={13} />} label="資材總數" value={`${total} 項`} />
            <PageMetaItem label="啟用中" value={`${activeCount} 項`} />
          </>
        }
        actions={
          <Button type="primary" icon={<Plus size={14} />} onClick={handleAdd}>
            新增資材
          </Button>
        }
        filters={
          <FilterBar
            search={
              <Input
                placeholder="搜尋名稱 / 備注"
                prefix={<Search size={14} color={color.text.muted} />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            }
            filters={
              <Select
                placeholder="分類"
                value={filterCategory}
                onChange={setFilterCategory}
                allowClear
                style={{ width: 140 }}
                options={categoryOptions}
              />
            }
            extra={
              (searchText || filterCategory) && (
                <Button
                  type="text"
                  size="small"
                  icon={<RefreshCw size={13} />}
                  onClick={() => {
                    setSearchText('');
                    setFilterCategory(undefined);
                  }}
                >
                  重置
                </Button>
              )
            }
          />
        }
      >
        <Table<Material>
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={isLoading}
          scroll={{ x: 800 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t) => `共 ${t} 筆資料`,
          }}
        />
      </ListPageShell>

      {/* 新增/編輯 Modal */}
      <Modal
        title={editingMaterial ? '編輯資材' : '新增資材'}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        footer={null}
        width={960}
        destroyOnHidden
      >
        <MaterialForm
          item={editingMaterial}
          onClose={() => {
            setModalOpen(false);
            form.resetFields();
            void qc.invalidateQueries({ queryKey: ['settings-materials'] });
          }}
          form={form}
        />
      </Modal>
    </>
  );
}

/**
 * 資材新增/編輯表單。
 * ingredients / approvedSchemes 使用 JSON textarea；M1 改為結構化輸入。
 */
function MaterialForm({
  item,
  onClose,
  form,
}: {
  item: Material | null;
  onClose: () => void;
  form: ReturnType<typeof Form.useForm<Record<string, unknown>>>[0];
}) {
  const qc = useQueryClient();

  /** 新增資材 Mutation */
  const create = useMutation({
    mutationFn: (v: unknown) => apiClient.post('/api/settings/materials', v),
    onSuccess: () => {
      message.success('資材已新增');
      void qc.invalidateQueries({ queryKey: ['settings-materials'] });
      onClose();
    },
  });

  /** 更新資材 Mutation */
  const update = useMutation({
    mutationFn: (v: unknown) => apiClient.patch(`/api/settings/materials/${item!.id}`, v),
    onSuccess: () => {
      message.success('資材已更新');
      void qc.invalidateQueries({ queryKey: ['settings-materials'] });
      onClose();
    },
  });

  /**
   * 表單提交前置處理：解析 JSON textarea 字串。
   */
  function handleFinish(values: Record<string, unknown>) {
    const payload: Record<string, unknown> = { ...values };

    for (const key of ['ingredientsJson', 'approvedSchemesJson']) {
      const val = values[key];
      if (typeof val === 'string' && val.trim()) {
        try {
          payload[key.replace('Json', '')] = JSON.parse(val);
        } catch {
          payload[key.replace('Json', '')] = val;
        }
      }
      delete payload[key];
    }

    if (item) {
      update.mutate(payload);
    } else {
      create.mutate(payload);
    }
  }

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={item ? {} : { isActive: true }}
      onFinish={handleFinish}
      style={{ marginTop: 16 }}
    >
      {/* 分類 + 資材名稱 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="category" label="分類" rules={[{ required: true, max: 50 }]}>
            <Input placeholder="pesticide / fertilizer / additive …" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="name" label="資材名稱" rules={[{ required: true, max: 200 }]}>
            <Input />
          </Form.Item>
        </Col>
      </Row>

      {/* 啟用狀態（半欄，與備注說明分開） */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="isActive" label="啟用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      {/* 備注說明（全寬 textarea） */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="notes" label="備注說明">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Col>
      </Row>

      {/* 成份 JSON（全寬 textarea） */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="ingredientsJson" label="成份（JSON；P0 可留空，M1 改為結構化輸入）">
            <Input.TextArea rows={3} placeholder='[{"name": "有效成份A", "ratio": "50%"}]' />
          </Form.Item>
        </Col>
      </Row>

      {/* 核可方案 JSON（全寬 textarea） */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="approvedSchemesJson" label="核可方案（JSON 陣列）">
            <Input.TextArea rows={2} placeholder='["TOP", "TAP"]' />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item style={{ marginBottom: 0 }}>
        <Space>
          <Button type="primary" htmlType="submit" loading={create.isPending || update.isPending}>
            {item ? '儲存' : '新增'}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}
